import React from "react"
import Map, { MapProps, MapProvider, Point, ViewStateChangeEvent } from "react-map-gl"
import type { MapRef } from "react-map-gl"
import { MAPBOX_TOKEN } from "../lib/config"
import { useUpdateEffect } from "../lib/hooks/useUpdateEffect"
import { Bbox } from "../lib/helpers/map"
import { useMapSettings } from "../components/providers/MapSettingsProvider"

interface PaddingOptions {
  top: number
  bottom: number
  left: number
  right: number
}

interface FitBoundsOptions {
  linear?: boolean
  easing?: (time: number) => number
  padding?: number | PaddingOptions
  offset?: Point | [number, number]
  maxZoom?: number
  duration?: number
}

interface MapboxProps extends MapProps {
  children?: React.ReactNode
  fitBounds?: Bbox
  fitBoundsOptions?: FitBoundsOptions
}

export type MapViewStateChangeEvent = ViewStateChangeEvent & {
  fitboundUpdate?: boolean
}

const Mapbox = React.forwardRef<MapRef, MapboxProps>(
  ({ children, fitBounds, fitBoundsOptions, ...props }, outerRef) => {
    const { mapboxStyle } = useMapSettings()
    const innerRef = React.useRef<MapRef>(null)

    React.useImperativeHandle(outerRef, () => innerRef.current!, [])

    useUpdateEffect(() => {
      if (!innerRef.current || !fitBounds) return
      const map = innerRef.current
      map.fitBounds(fitBounds, fitBoundsOptions, { fitboundUpdate: true })
    }, [fitBounds, innerRef.current])

    return (
      <MapProvider>
        <Map
          ref={innerRef}
          {...props}
          initialViewState={{
            bounds: fitBounds,
            fitBoundsOptions,
          }}
          reuseMaps
          mapboxAccessToken={MAPBOX_TOKEN}
          mapStyle={`mapbox://styles/${mapboxStyle}`}
        >
          {children}
        </Map>
      </MapProvider>
    )
  },
)

Mapbox.displayName = "Mapbox"

export default Mapbox
